import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Share2, Lock, Copy, Eye, X, Loader2, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading } from "@/pages/_shared";
import { Switch } from "@/components/ui/switch";
import api, { formatApiErrorDetail } from "@/lib/api";

const CLASS_COLORS = {
  "INTERNAL": "#0ea5e9", "CONFIDENTIAL": "#8b5cf6",
  "SENSITIVE FINANCIAL": "#1a5fd0", "RESTRICTED IDENTITY": "#e11d48",
};

function PreviewModal({ token, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/passport/shared/${token}`).then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : "Unavailable"));
  }, [token]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="passport-preview-modal">
        <div className="flex items-center justify-between">
          <div className="font-head text-lg font-extrabold text-slate-900">Partner Preview</div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {err && <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-600">{err}</div>}
        {!err && !data && <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-[#1a5fd0]" /></div>}
        {data && (
          <>
            <div className="mt-2 flex items-center gap-2"><StatusChip state={data.state} /><StatusChip state={data.delivery_state} /></div>
            <div className="mt-1 text-[12px] text-slate-500">Shared with <b>{data.partner_name}</b></div>
            <div className="mt-4 space-y-2">
              {data.sections.map((s) => (
                <div key={s.key} className="rounded-lg border border-slate-100 px-3 py-2">
                  <div className="text-[12px] font-semibold text-slate-800">{s.title}</div>
                  {s.fields.slice(0, 3).map((f) => <div key={f.label} className="flex justify-between text-[11px]"><span className="text-slate-400">{f.label}</span><span className="text-slate-700">{f.value}</span></div>)}
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">{data.note}</div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CapitalPassport() {
  const [sections, setSections] = useState(null);
  const [shares, setShares] = useState([]);
  const [partner, setPartner] = useState("");
  const [selected, setSelected] = useState([]);
  const [expires, setExpires] = useState(14);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [plan, setPlan] = useState(null);

  const load = useCallback(async () => {
    try {
      const mp = await api.get("/payments/my-plan");
      setPlan(mp.data.plan);
      if (mp.data.plan !== "Expand") { setSections([]); return; }
      const [p, s] = await Promise.all([api.get("/passport"), api.get("/passport/shares")]);
      setSections(p.data.sections);
      setShares(s.data.items);
    } catch { setSections([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, shared) => {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, shared } : s)));
    if (!shared) setSelected((sel) => sel.filter((k) => k !== key));
    try { await api.put("/passport/permissions", { section_key: key, shared }); toast.success(`Sharing ${shared ? "enabled" : "disabled"} for ${key}`); }
    catch { toast.error("Could not update permission"); load(); }
  };

  const createShare = async () => {
    if (!partner.trim()) return toast.error("Enter a partner name");
    if (selected.length === 0) return toast.error("Select at least one enabled section");
    setCreating(true);
    try {
      await api.post("/passport/shares", { partner_name: partner, sections: selected, expires_days: Number(expires) });
      toast.success("Secure share link created (SANDBOX)");
      setPartner(""); setSelected([]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : "Could not create share"); } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/passport/shares/${id}/revoke`); toast.success("Share revoked"); await load(); }
    catch { toast.error("Could not revoke"); }
  };

  if (plan && plan !== "Expand") {
    return (
      <div className="space-y-6 animate-fade-up" data-testid="capital-passport-page">
        <PageHeader eyebrow="Capital Passport™" title="Capital Passport"
          subtitle="A reusable, permission-controlled capital profile you can securely share with capital partners." />
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center" data-testid="passport-upgrade-lock">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#b0862f]/12"><Lock size={22} className="text-[#b0862f]" /></span>
          <div className="font-head text-lg font-bold text-slate-900">Capital Passport is an Expand feature</div>
          <p className="max-w-md text-[13px] text-slate-500">Upgrade to the Expand plan to build a permission-controlled capital profile and share it securely with lenders and partners.</p>
          <Link to="/pricing" data-testid="passport-upgrade-cta" className="mt-1 inline-flex items-center gap-2 rounded-xl bg-[#0f172a] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#1a5fd0]">Upgrade to Expand <ArrowRight size={15} /></Link>
        </Card>
      </div>
    );
  }
  if (!sections) return <Loading />;
  const enabled = sections.filter((s) => s.shared);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="capital-passport-page">
      <PageHeader eyebrow="Capital Passport™" title="Capital Passport"
        subtitle="A reusable, permission-controlled capital profile. You decide exactly which sections a partner can see — nothing is shared without your consent."
        badge={<StatusChip state="SANDBOX" />} />

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Permissions */}
        <Card className="p-5">
          <SectionTitle icon={Lock}>Section Permissions</SectionTitle>
          <div className="space-y-2">
            {sections.map((s) => (
              <div key={s.key} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3" data-testid="passport-section-row">
                <div>
                  <div className="text-[13px] font-semibold text-slate-800">{s.title}</div>
                  <span className="font-mono text-[9px] font-bold uppercase tracking-wider" style={{ color: CLASS_COLORS[s.data_class] }}>{s.data_class}</span>
                </div>
                <Switch checked={s.shared} onCheckedChange={(v) => toggle(s.key, v)} data-testid={`passport-toggle-${s.key}`} />
              </div>
            ))}
          </div>
        </Card>

        {/* Create share */}
        <div className="space-y-5">
          <Card className="p-5">
            <SectionTitle icon={Share2}>Share with a Capital Partner</SectionTitle>
            <input value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="Partner name (e.g. Partner Bank A)" data-testid="share-partner-input"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1a5fd0]" />
            <div className="mt-3">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">Sections to share (enabled only)</div>
              <div className="flex flex-wrap gap-1.5">
                {enabled.length === 0 && <span className="text-[12px] text-slate-400">Enable sections on the left first.</span>}
                {enabled.map((s) => {
                  const on = selected.includes(s.key);
                  return (
                    <button key={s.key} onClick={() => setSelected((sel) => on ? sel.filter((k) => k !== s.key) : [...sel, s.key])} data-testid={`share-section-${s.key}`}
                      className={`rounded-md border px-2 py-1 text-[11px] transition-colors ${on ? "border-[#1a5fd0] bg-blue-50 text-[#1a5fd0]" : "border-slate-200 text-slate-500"}`}>
                      {s.title}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[12px] text-slate-500">Expires in</span>
              <input type="number" min="1" max="90" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="share-expires-input"
                className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
              <span className="text-[12px] text-slate-500">days</span>
            </div>
            <button onClick={createShare} disabled={creating} data-testid="create-share-btn"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#1a5fd0] py-2.5 text-[13px] font-semibold text-white disabled:opacity-50">
              {creating ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} Create secure share link
            </button>
            <div className="mt-2 text-[11px] text-slate-400">Sharing generates a secure, expiring token. Delivery to a real partner is <b>PROVIDER APPROVAL PENDING</b> — not a live integration.</div>
          </Card>
        </div>
      </div>

      {/* Active shares */}
      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3.5"><SectionTitle icon={BadgeCheck}>Active & Past Shares</SectionTitle></div>
        <div className="divide-y divide-slate-100">
          {shares.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-400">No shares yet.</div>}
          {shares.map((sh) => (
            <div key={sh.id} className="flex items-center gap-3 px-5 py-3.5" data-testid="share-row">
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-slate-800">{sh.partner_name}</div>
                <div className="font-mono text-[10px] text-slate-400">{sh.sections.length} sections · {sh.access_count} views · token ••{sh.token.slice(-6)}</div>
              </div>
              <StatusChip state={sh.status === "ACTIVE" ? (sh.expired ? "COMING SOON" : "SANDBOX") : "NOT CONNECTED"} />
              <button onClick={() => setPreview(sh.token)} data-testid="share-preview-btn" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#1a5fd0]"><Eye size={16} /></button>
              {sh.status === "ACTIVE" && (
                <button onClick={() => revoke(sh.id)} data-testid="share-revoke-btn" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-500"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {preview && <PreviewModal token={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
